import { useMutation } from '@tanstack/react-query';
import { apiClient } from 'api';
import { toast } from 'sonner';

export interface WaitlistPayload {
  full_name: string;
  email: string;
  job_role: string;
  country_code: string;
  phone_number: string;
}

export interface WaitlistResponse {
  message: string;
  data: {
    id: number;
    full_name: string;
    email: string;
    job_role: string;
    country_code: string;
    phone_number: string;
  };
}

const extractErrorMessage = (errorData: any): string => {
  if (errorData?.error?.message && typeof errorData.error.message === 'object') {
    const fieldErrors = errorData.error.message;
    const errorMessages: string[] = [];

    Object.entries(fieldErrors).forEach(([field, errors]) => {
      if (Array.isArray(errors)) {
        const fieldLabel = field === 'phone_number' ? 'Phone number' :
                          field === 'full_name' ? 'Full name' :
                          field === 'email' ? 'Email' :
                          field === 'job_role' ? 'Job role' :
                          field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        errors.forEach((error: string) => {
          errorMessages.push(`${fieldLabel}: ${error}`);
        });
      }
    });

    return errorMessages.length > 0 ? errorMessages.join('. ') : 'Please check your information and try again.';
  }

  const extractedMessage = errorData?.error?.message ??
                          errorData?.message ??
                          errorData?.detail;

  return typeof extractedMessage === 'string'
    ? extractedMessage
    : 'Please check your information and try again.';
};

export const useWaitlist = () => {
  return useMutation({
    mutationFn: async (payload: WaitlistPayload) => {
      const response = await apiClient.post<WaitlistResponse>('/auth/waitlist/', payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Successfully joined the waitlist!')
    },
    onError: (err: any) => {
      let errorMessage = 'Failed to join waitlist. Please try again.';

      if (err?.response) {
        const errData = err.response.data;
        const status = err.response.status;

        if (status === 400) {
          errorMessage = extractErrorMessage(errData);
        } else if (status === 409) {
          errorMessage = 'You are already on the waitlist!';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = extractErrorMessage(errData);
        }
      } else if (err?.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error(errorMessage);
      console.error('Waitlist request error:', err);
    },
  });
};
